// src/pages/appointment-calendar/components/CalendarView.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

/* =========================
   Time & formatting utils
   ========================= */
const toLocal = (isoOrDate) => {
  if (!isoOrDate) return null;
  if (isoOrDate instanceof Date) return isNaN(isoOrDate) ? null : isoOrDate;
  const d = new Date(isoOrDate);
  return isNaN(d) ? null : d;
};

// strict HH:MM (24h)
const fmtHM = (d) => {
  if (!d) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const stripDate = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(d.getDate() + n);
  return x;
};
const daysInMonth = (d) =>
  new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
const minutesFromMidnight = (d) => d.getHours() * 60 + d.getMinutes();
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const truncateText = (s, max = 24) =>
  s ? (s.length > max ? s.slice(0, max - 1) + "…" : s) : "";

// (legacy) slugify pentru filtrarea pe staff ("Maria Popescu" -> "maria-popescu")
// Nu mai folosim slug-ul ca identificator; filtrarea se face pe staffId.
const slugifyName = (s = "") =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/* =========================
   Status helpers (bulina)
   ========================= */

const normalizeStatus = (status) => {
  const s = (status || "").toLowerCase().trim();
  if (s === "confirmed") return "booked";
  if (["booked", "cancelled", "completed"].includes(s)) return s;
  return "booked";
};

const getStatusDotClass = (status) => {
  const s = normalizeStatus(status);
  switch (s) {
    case "booked": // Confirmată
      return "bg-emerald-500";
    case "cancelled":
      return "bg-red-500";
    case "completed":
      return "bg-blue-500";
    default:
      return "bg-gray-300";
  }
};

/* =========================
   Grid & layout constants
   ========================= */
const DEFAULT_SLOT_MINUTES = 15;

// 3 linii de text (leading-4 = 16px) => 3 * 16 = 48px
const SLOT_LINE_PX = 12;
const SLOT_HEIGHT = 3 * SLOT_LINE_PX; // 36px


/* =========================
   Working hours helpers
   ========================= */

// mapping: JS getDay(): 0=Sun..6=Sat  ->  0=Luni..6=Duminică (ca în backend)
const getWeekdayIndex = (d) => {
  const js = d.getDay(); // 0=Sun
  return (js + 6) % 7; // 0=Mon
};

/**
 * Construiește config global pentru programul de lucru:
 * - dacă nu avem nimic (workingHours = null/undefined) => full-day (00:00–24:00)
 * - dacă avem obiect `recurring`, îl considerăm *config activ*, chiar dacă toate zilele sunt închise.
 *
 * slotMinutes vine din props (alegere utilizator).
 */
function buildWorkingConfig(workingHours, slotMinutesProp) {
  const slotMinutes =
    Number(slotMinutesProp) > 0 ? Number(slotMinutesProp) : DEFAULT_SLOT_MINUTES;

  const fullDaySlots = Math.ceil((24 * 60) / slotMinutes);

  const recurring =
    (workingHours && workingHours.recurring) ||
    (workingHours && typeof workingHours === "object" ? workingHours : null);

  const base = {
    hasConfig: false,
    slotMinutes,
    openMinutes: 0,
    closeMinutes: 24 * 60,
    totalSlots: fullDaySlots,
    days: {},
  };

  // nu avem deloc date → calendar full-day
  if (!recurring || typeof recurring !== "object") {
    return base;
  }

  const intervals = [];
  const days = {};

  for (let wd = 0; wd < 7; wd++) {
    const arr = Array.isArray(recurring[String(wd)]) ? recurring[String(wd)] : [];
    if (!arr.length) {
      days[wd] = { isClosed: true };
      continue;
    }
    const first = arr[0];
    const [sh, sm] = String(first.start || "00:00").split(":").map(Number);
    const [eh, em] = String(first.end || "00:00").split(":").map(Number);
    const sMin = sh * 60 + sm;
    const eMin = eh * 60 + em;
    if (eMin <= sMin) {
      days[wd] = { isClosed: true };
      continue;
    }
    intervals.push({ wd, sMin, eMin });
    days[wd] = { isClosed: false, startMinutes: sMin, endMinutes: eMin };
  }

  // avem config (recurring există), chiar dacă toate zilele sunt închise
  if (!intervals.length) {
    const openMinutes = 8 * 60; // 08:00
    const closeMinutes = 20 * 60; // 20:00
    const duration = Math.max(slotMinutes, closeMinutes - openMinutes);
    const totalSlots = Math.ceil(duration / slotMinutes);

    return {
      hasConfig: true,
      slotMinutes,
      openMinutes,
      closeMinutes,
      totalSlots,
      days,
    };
  }

  const openMinutes = Math.min(...intervals.map((x) => x.sMin));
  const closeMinutes = Math.max(...intervals.map((x) => x.eMin));
  const duration = Math.max(slotMinutes, closeMinutes - openMinutes);
  const totalSlots = Math.ceil(duration / slotMinutes);

  return {
    hasConfig: true,
    slotMinutes,
    openMinutes,
    closeMinutes,
    totalSlots,
    days,
  };
}

function getDayMeta(workingConfig, date) {
  if (!workingConfig) return { isClosed: false };
  const wd = getWeekdayIndex(date);
  const info = workingConfig.days?.[wd];

  // dacă nu avem config (workingHours = null) -> toate zilele deschise, full-day
  if (!workingConfig.hasConfig || !info) {
    return { isClosed: false };
  }

  return info.isClosed ? { isClosed: true } : { isClosed: false, ...info };
}

function buildVisibleTimeSlots(cfg) {
  const slotMinutes = cfg?.slotMinutes ?? DEFAULT_SLOT_MINUTES;
  const openMinutes = cfg?.openMinutes ?? 0;
  const totalSlots =
    cfg?.totalSlots ??
    Math.ceil(((cfg?.closeMinutes ?? 24 * 60) - openMinutes) / slotMinutes);

  return Array.from({ length: totalSlots }, (_, i) => {
    const minutes = openMinutes + i * slotMinutes;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const label = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    const isHour = m === 0;
    return { label, isHour, minutes };
  });
}

/* =========================
   Normalize appointment
   ========================= */
const mapAppt = (it) => {
  const start = toLocal(it.start_at || it.start || it.startAt);
  let end = toLocal(it.end_at || it.end || it.endAt);
  if (start && (!end || end <= start))
    end = new Date(start.getTime() + 30 * 60 * 1000);

  const staffStr = it.staff_name || it.staffName || "";
  const staffId =
    it.staff_id ??
    it.staffId ??
    it.staff_user_id ??
    (typeof it.staff === "number" ? it.staff : it.staff?.id) ??
    it._raw?.staff_id ??
    it._raw?.staff_user_id ??
    (typeof it._raw?.staff === "number" ? it._raw.staff : it._raw?.staff?.id) ??
    it.raw?.staff_id ??
    it.raw?.staff_user_id ??
    (typeof it.raw?.staff === "number" ? it.raw.staff : it.raw?.staff?.id) ??
    null;
  const status = normalizeStatus(it.status || "booked");

	  const serviceNames = Array.isArray(it.service_names)
	    ? it.service_names
	    : Array.isArray(it.serviceNames)
	      ? it.serviceNames
	      : [];
	  const combinedService = serviceNames.length ? serviceNames.filter(Boolean).join(", ") : "";

  return {
    id: it.id,
    start,
    end,
    status,
    client: it.client_name || it.clientName || "",
	    service: combinedService || it.service_name || it.serviceName || "",
    staff: staffStr,
    staffId: staffId != null ? String(staffId) : "",
    raw: it,
  };
};

/* =========================
   Helpers pentru plasarea pe grid
   ========================= */
function rowsForEventInDay(evt, dayDate, cfg) {
  const slotMinutes = cfg?.slotMinutes ?? DEFAULT_SLOT_MINUTES;

  const dayStart = new Date(
    dayDate.getFullYear(),
    dayDate.getMonth(),
    dayDate.getDate(),
    0,
    0,
    0,
    0
  );

  const openMinutes = cfg?.openMinutes ?? 0;
  const closeMinutes = cfg?.closeMinutes ?? 24 * 60;

  const dayOpen = new Date(dayStart.getTime() + openMinutes * 60 * 1000);
  const dayClose = new Date(dayStart.getTime() + closeMinutes * 60 * 1000);

  const startClamped = new Date(Math.max(evt.start.getTime(), dayOpen.getTime()));
  let endClamped = new Date(Math.min(evt.end.getTime(), dayClose.getTime()));

  if (endClamped <= startClamped) {
    endClamped = new Date(startClamped.getTime() + slotMinutes * 60 * 1000);
  }

  const startMin = minutesFromMidnight(startClamped);
  const endMin = minutesFromMidnight(endClamped);

  const relStart = startMin - openMinutes;
  const relEnd = endMin - openMinutes;

  const rowStart = Math.floor(relStart / slotMinutes) + 1;
  const rowEnd = Math.max(rowStart + 1, Math.ceil(relEnd / slotMinutes) + 1);

  return { rowStart, rowEnd };
}

function layoutDayLanes(events, dayDate, cfg) {
  if (!events.length) {
    return { items: [], lanesCount: 1 };
  }

  const sorted = [...events].sort(
    (a, b) => a.start - b.start || a.end - b.end
  );
  const lanesEnd = [];
  const out = [];

  sorted.forEach((a) => {
    const { rowStart, rowEnd } = rowsForEventInDay(a, dayDate, cfg);
    let lane = 0;
    while (lane < lanesEnd.length && lanesEnd[lane] > rowStart) lane++;
    lanesEnd[lane] = rowEnd;
    out.push({ ...a, rowStart, rowEnd, lane });
  });

  const lanesCount = Math.max(1, lanesEnd.length || 1);
  return { items: out, lanesCount };
}

/* =========================
   Day View (interval per zi)
   ========================= */
function DayView({
  embedded = false,
  date,
  items,
  selectedStaff = "all",
  onAppointmentClick,
  onDateSelect,
  workingConfig,
  timeOffPeriods = [],
}) {
  const { t } = useTranslation();

  const dayMeta = getDayMeta(workingConfig, date);

  const dayConfig = useMemo(() => {
    const slotMinutes = workingConfig?.slotMinutes ?? DEFAULT_SLOT_MINUTES;

    // dacă ziua are propriul interval -> îl folosim
    if (
      dayMeta &&
      !dayMeta.isClosed &&
      typeof dayMeta.startMinutes === "number" &&
      typeof dayMeta.endMinutes === "number" &&
      dayMeta.endMinutes > dayMeta.startMinutes
    ) {
      const openMinutes = dayMeta.startMinutes;
      const closeMinutes = dayMeta.endMinutes;
      const duration = Math.max(slotMinutes, closeMinutes - openMinutes);
      const totalSlots = Math.ceil(duration / slotMinutes);
      return {
        slotMinutes,
        openMinutes,
        closeMinutes,
        totalSlots,
      };
    }

    // altfel: cădem pe intervalul global (sau 00–24)
    const openMinutes = workingConfig?.openMinutes ?? 0;
    const closeMinutes = workingConfig?.closeMinutes ?? 24 * 60;
    const duration = Math.max(slotMinutes, closeMinutes - openMinutes);
    const totalSlots = Math.ceil(duration / slotMinutes);
    return {
      slotMinutes,
      openMinutes,
      closeMinutes,
      totalSlots,
    };
  }, [workingConfig, dayMeta]);

  const timeSlots = useMemo(
    () => buildVisibleTimeSlots(dayConfig),
    [dayConfig]
  );
  const totalSlots = dayConfig.totalSlots;

  const list = useMemo(() => {
    const all = items
      .map(mapAppt)
      .filter((a) => a.start && sameDay(a.start, date));
    const filtered =
      !selectedStaff || selectedStaff === "all"
        ? all
        : all.filter((a) => String(a.staffId) === String(selectedStaff));
    return filtered.sort((a, b) => a.start - b.start);
  }, [items, date, selectedStaff]);

  const { items: laidOut, lanesCount } = useMemo(
    () => layoutDayLanes(list, date, dayConfig),
    [list, date, dayConfig]
  );

  if (dayMeta.isClosed) {
    return (
      <div className={`${embedded ? "border-0 overflow-visible" : "border rounded-xl overflow-hidden"}`}>
        <div className={`flex bg-muted text-foreground/80 text-sm ${embedded ? "px-2" : "px-4"} py-2`}>
          <div className={`${embedded ? "w-12" : "w-20"} shrink-0`}>{t("calendar.colTime")}</div>
          <div className="flex-1">{t("calendar.colAppointments")}</div>
        </div>
        <div
          className="flex items-center justify-center"
          style={{
            minHeight: 180,
            background: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(100,116,139,0.12) 8px, rgba(100,116,139,0.12) 16px)",
          }}
        >
          <span className="text-muted-foreground text-sm font-semibold bg-muted rounded-md px-3 py-1.5 border border-border">
            Locația este închisă în această zi
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? "border-0 overflow-visible" : "border rounded-xl overflow-hidden"}`}>

      <div className={`flex bg-muted text-foreground/80 text-sm ${embedded ? "px-2" : "px-4"} py-2`}>

        <div className={`${embedded ? "w-12" : "w-20"} shrink-0`}>{t("calendar.colTime")}</div>

        <div className="flex-1">{t("calendar.colAppointments")}</div>
      </div>

      <div
        className="grid"
        style={{ gridTemplateColumns: `${embedded ? "52px" : "80px"} 1fr` }}
      >

        {/* Stânga: ore pentru ziua curentă */}
        <div
          className="grid border-r"
          style={{
            gridTemplateRows: `repeat(${totalSlots}, ${SLOT_HEIGHT}px)`,
          }}
        >
          {timeSlots.map((slot) => (
            <div
              key={slot.label}
              className={`${embedded ? "px-1" : "px-2"} border-b flex items-start`}

            >
              <span
                className={
                  slot.isHour
                    ? "text-[10px] text-foreground/60"
                    : "text-[9px] text-foreground/40"
                }

              >
                {slot.isHour ? slot.label : `:${slot.label.slice(-2)}`}
              </span>
            </div>
          ))}
        </div>

        {/* Dreapta: programări în ziua curentă */}
        <div
          className="grid relative"
          style={{
            gridTemplateRows: `repeat(${totalSlots}, ${SLOT_HEIGHT}px)`,
            gridTemplateColumns: `repeat(${lanesCount}, 1fr)`,
          }}
          onClick={(e) => {
            if (isOnLeave(timeOffPeriods, selectedStaff, date)) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const idx = clamp(
              Math.floor(y / SLOT_HEIGHT),
              0,
              totalSlots - 1
            );

            const openMinutes = dayConfig.openMinutes ?? 0;
            const slotMinutes =
              dayConfig.slotMinutes ?? DEFAULT_SLOT_MINUTES;
            const minutes = openMinutes + idx * slotMinutes;
            const h = String(Math.floor(minutes / 60)).padStart(2, "0");
            const m = String(minutes % 60).padStart(2, "0");

            onDateSelect?.(date, `${h}:${m}`);
          }}
          title="Click pentru a crea programare"
        >
          {timeSlots.map((slot, i) => (
            <div
              key={slot.label}
              className={`border-b ${slot.isHour ? "border-border" : "border-border/40"
                } col-span-full`}
              style={{ gridRow: `${i + 1} / ${i + 2}` }}
            />
          ))}

          {isOnLeave(timeOffPeriods, selectedStaff, date) && (
            <div
              className="pointer-events-none absolute inset-0 col-span-full z-10 flex flex-col items-center justify-center gap-1"
              style={{ background: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(251,146,60,0.13) 8px, rgba(251,146,60,0.13) 16px)" }}
            >
              <span className="text-orange-500 text-xs font-semibold bg-orange-50 dark:bg-orange-950/60 rounded px-2 py-0.5 border border-orange-300">
                În concediu
              </span>
            </div>
          )}

          {laidOut.map((a) => (
            <button
              key={
                a.id ||
                `${a.client}-${a.service}-${a.start?.toISOString()}`
              }
              type="button"
              className="rounded-md border bg-card shadow-sm overflow-hidden text-left px-1 py-0.5 leading-tight hover:bg-accent"
              style={{
                gridRow: `${a.rowStart} / ${a.rowEnd}`,
                gridColumn: `${a.lane + 1} / span 1`,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onAppointmentClick?.(a.raw);
              }}
              title={`${fmtHM(a.start)}–${fmtHM(a.end)}`}
            >
              <div className="flex items-center gap-1 text-[12px] font-medium truncate">
                <span
                  className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${getStatusDotClass(
                    a.status
                  )}`}
                />
                <span className="truncate">
                  {a.client || "Client"} •{" "}
                  {truncateText(a.service || "Serviciu", 28)}
                </span>
              </div>
              <div className="text-[11px] text-foreground/60 truncate">
                {a.staff ? a.staff : ""}
              </div>

            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* =========================
   Week View (interval global)
   ========================= */
function WeekView({
  embedded = false,
  date,
  items,
  selectedStaff = "all",
  onAppointmentClick,
  onDateSelect,
  workingConfig,
  timeOffPeriods = [],
}) {
  const { t, i18n } = useTranslation();

  const startOfWeek = useMemo(() => {
    const d = new Date(date);
    const wd = (d.getDay() + 6) % 7; // 0 = luni
    d.setDate(d.getDate() - wd);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [date]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(startOfWeek, i)),
    [startOfWeek]
  );

  const slotMinutes = workingConfig?.slotMinutes ?? DEFAULT_SLOT_MINUTES;
  const totalSlots =
    workingConfig?.totalSlots ??
    Math.ceil(
      ((workingConfig?.closeMinutes ?? 24 * 60) -
        (workingConfig?.openMinutes ?? 0)) /
      slotMinutes
    );

  const timeSlots = useMemo(
    () => buildVisibleTimeSlots(workingConfig),
    [workingConfig]
  );

  const buckets = useMemo(() => {
    const x = {};
    days.forEach((d) => (x[stripDate(d).toDateString()] = []));
    items.map(mapAppt).forEach((a) => {
      if (!a.start) return;
      if (selectedStaff && selectedStaff !== "all" && String(a.staffId) !== String(selectedStaff))
        return;
      const key = stripDate(a.start).toDateString();
      if (x[key]) x[key].push(a);
    });
    Object.values(x).forEach((arr) =>
      arr.sort((a, b) => a.start - b.start)
    );
    return x;
  }, [items, days, selectedStaff]);

  const dayLabel = (d) =>
    d.toLocaleDateString(i18n.language || "ro", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });

  const weekDayHeaderRaw = t("calendar.weekDaysShort", { returnObjects: true });
  const weekDayHeader = Array.isArray(weekDayHeaderRaw)
    ? weekDayHeaderRaw
    : ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"];

  return (
    <div className="border rounded-xl overflow-clip">
      {/* header: ore + zile */}
      <div
        className="grid bg-muted text-xs text-foreground/70 px-2 py-2 sticky z-10"
        style={{ gridTemplateColumns: `${embedded ? "52px" : "80px"} repeat(7, minmax(0, 1fr))`, top: 'var(--header-total)' }}
      >
        <div className="px-2">Ora</div>
        {days.map((d, idx) => (
          <div
            key={d.toDateString()}
            className="px-2 text-center font-medium"
          >
            {weekDayHeader[idx]} • {dayLabel(d)}
          </div>
        ))}
      </div>

      {/* corp: col. ore + 7 coloane zile */}
      <div
        className="grid"
        style={{ gridTemplateColumns: `${embedded ? "52px" : "80px"} repeat(7, minmax(0, 1fr))` }}
      >

        {/* col. ore */}
        <div
          className="grid border-r"
          style={{
            gridTemplateRows: `repeat(${totalSlots}, ${SLOT_HEIGHT}px)`,
          }}
        >
          {timeSlots.map((slot) => (
            <div
              key={slot.label}
              className={`${embedded ? "px-1" : "px-2"} border-b flex items-start`}
            >
              <span
                className={
                  slot.isHour
                    ? "text-[10px] text-foreground/60"
                    : "text-[9px] text-foreground/40"
                }

              >
                {slot.isHour ? slot.label : `:${slot.label.slice(-2)}`}
              </span>
            </div>
          ))}
        </div>

        {/* 7 coloane, câte un grid pe zi */}
        {days.map((d) => {
          const key = stripDate(d).toDateString();
          const dayEvents = buckets[key] || [];
          const { items: laidOut, lanesCount } = layoutDayLanes(
            dayEvents,
            d,
            workingConfig
          );
          const meta = getDayMeta(workingConfig, d);
          const isClosed = meta.isClosed;

          return (
            <div
              key={key}
              className={`relative grid border-r last:border-r-0 ${isClosed ? "bg-muted/30" : ""
                }`}
              style={{
                gridTemplateRows: `repeat(${totalSlots}, ${SLOT_HEIGHT}px)`,
                gridTemplateColumns: `repeat(${lanesCount}, 1fr)`,
              }}
              onClick={
                isClosed
                  ? undefined
                  : (e) => {
                    const rect =
                      e.currentTarget.getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const idx = clamp(
                      Math.floor(y / SLOT_HEIGHT),
                      0,
                      totalSlots - 1
                    );
                    const openMinutes =
                      workingConfig?.openMinutes ?? 0;
                    const minutes = openMinutes + idx * slotMinutes;
                    const hh = String(
                      Math.floor(minutes / 60)
                    ).padStart(2, "0");
                    const mm = String(minutes % 60).padStart(2, "0");
                    onDateSelect?.(d, `${hh}:${mm}`);
                  }
              }
              title={
                isClosed
                  ? "Locația este închisă în această zi."
                  : `Creează programare pe ${d.toLocaleDateString()}`
              }
            >
              {/* grilă */}
              {timeSlots.map((slot, i) => (
                <div
                  key={`bg-${key}-${slot.label}`}
                  className={`border-b ${slot.isHour ? "border-border" : "border-border/40"
                    } col-span-full`}
                  style={{ gridRow: `${i + 1} / ${i + 2}` }}
                />
              ))}

              {/* overlay "Închis" */}
              {isClosed && (
                <div
                  className="pointer-events-none absolute inset-0 flex items-center justify-center"
                  style={{ background: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(100,116,139,0.12) 8px, rgba(100,116,139,0.12) 16px)" }}
                >
                  <span className="text-muted-foreground text-[10px] font-semibold bg-muted rounded px-1.5 py-0.5 border border-border">
                    Închis
                  </span>
                </div>
              )}

              {/* overlay concediu */}
              {!isClosed && (() => {
                const onLeave = selectedStaff && selectedStaff !== "all"
                  ? isOnLeave(timeOffPeriods, selectedStaff, d)
                  : false;
                const names = selectedStaff === "all" ? leaveNamesForDay(timeOffPeriods, d) : [];
                return (
                  <>
                    {onLeave && (
                      <div
                        className="pointer-events-none absolute inset-0 col-span-full z-10 flex items-center justify-center"
                        style={{ background: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(251,146,60,0.12) 8px, rgba(251,146,60,0.12) 16px)" }}
                      >
                        <span className="text-orange-500 text-[10px] font-semibold bg-orange-50 dark:bg-orange-950/60 rounded px-1.5 py-0.5 border border-orange-300">
                          Concediu
                        </span>
                      </div>
                    )}
                    {!onLeave && names.length > 0 && (
                      <div className="pointer-events-none absolute top-0 left-0 right-0 z-10 flex flex-wrap gap-0.5 px-0.5 pt-0.5">
                        {names.map((name, i) => (
                          <span key={i} className="text-[9px] bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-300 rounded px-1 leading-4 border border-orange-200 truncate max-w-full">
                            {name} · concediu
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* evenimente */}
              {!isClosed &&
                laidOut.map((a) => (
                  <button
                    key={
                      a.id ||
                      `${a.client}-${a.service}-${a.start?.toISOString()}`
                    }
                    type="button"
                    className="rounded-sm border bg-card shadow-sm overflow-hidden text-left px-2 py-1 leading-tight hover:bg-accent"
                    style={{
                      gridRow: `${a.rowStart} / ${a.rowEnd}`,
                      gridColumn: `${a.lane + 1} / span 1`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAppointmentClick?.(a.raw);
                    }}
                    title={`${fmtHM(a.start)}–${fmtHM(a.end)}`}
                  >
                    <div className="flex items-center gap-1 text-[12px] font-medium truncate">
                      <span
                        className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${getStatusDotClass(
                          a.status
                        )}`}
                      />
                      <span className="truncate">
                        {a.client || "Client"}
                      </span>
                    </div>
                    <div className="text-[11px] text-foreground/60 truncate">
                      {truncateText(a.service || "Serviciu", 28)}
                      {a.staff ? ` • ${a.staff}` : ""}
                    </div>

                  </button>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =========================
   Month View
   ========================= */
function MonthView({
  date,
  items,
  selectedStaff = "all",
  onAppointmentClick,
  onDateSelect,
  timeOffPeriods = [],
}) {
  const { t } = useTranslation();
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const lastDay = daysInMonth(date);

  const shift = (first.getDay() + 6) % 7; // începe cu luni
  const totalCells = Math.ceil((shift + lastDay) / 7) * 7;

  const cells = Array.from({ length: totalCells }, (_, i) => {
    const day = i - shift + 1;
    const d = new Date(date.getFullYear(), date.getMonth(), day);
    return { d, inMonth: day >= 1 && day <= lastDay };
  });

  const byDay = useMemo(() => {
    const map = {};
    items.map(mapAppt).forEach((a) => {
      if (!a.start) return;
      if (
        selectedStaff &&
        selectedStaff !== "all" &&
        String(a.staffId) !== String(selectedStaff)
      )
        return;
      const key = `${a.start.getFullYear()}-${a.start.getMonth()}-${a.start.getDate()}`;
      (map[key] ||= []).push(a);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((x, y) => x.start - y.start)
    );
    return map;
  }, [items, selectedStaff]);

  const keyOf = (d) =>
    `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const weekDayHeaderRaw = t("calendar.weekDaysShort", { returnObjects: true });
  const weekDayHeader = Array.isArray(weekDayHeaderRaw)
    ? weekDayHeaderRaw
    : ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"];

  const MONTH_CELL_MIN_PX = 96;

  return (
    <div className="border rounded-xl overflow-hidden">
      <div className="grid grid-cols-7 text-xs text-center text-foreground/70">
        {weekDayHeader.map((w) => (
          <div key={w} className="px-2 py-2 font-medium">
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map(({ d, inMonth }, idx) => {
          const list = (byDay[keyOf(d)] || []).slice(0, 3);
          const extra = (byDay[keyOf(d)] || []).length - list.length;

          return (
            <div
              key={idx}
              className={`border p-2 ${inMonth
                ? "bg-background"
                : "bg-muted/20 text-foreground/50"
                } hover:bg-muted/40 transition cursor-pointer`}
              style={{ minHeight: MONTH_CELL_MIN_PX }}
              onClick={() => onDateSelect?.(d)}
              title={`Creează programare pe ${d.toLocaleDateString()}`}
            >
              <div className="text-xs font-medium mb-1">
                {d.getDate()}
              </div>
              {(() => {
                const names = leaveNamesForDay(timeOffPeriods, d);
                const filtered = selectedStaff && selectedStaff !== "all"
                  ? names.filter(() => isOnLeave(timeOffPeriods, selectedStaff, d))
                  : names;
                return filtered.length > 0 ? (
                  <div className="mb-1 flex flex-wrap gap-0.5">
                    {filtered.map((name, i) => (
                      <span key={i} className="text-[9px] bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-300 rounded px-1 leading-4 border border-orange-200 truncate max-w-full">
                        {selectedStaff !== "all" ? "Concediu" : `${name} · concediu`}
                      </span>
                    ))}
                  </div>
                ) : null;
              })()}
              <div className="space-y-1">
                {list.map((a) => (
                  <div
                    key={
                      a.id ||
                      `${a.client}-${a.service}-${a.start?.toISOString()}`
                    }
                    className="rounded border bg-card px-2 py-1 text-[11px] leading-4 shadow-sm hover:shadow truncate"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAppointmentClick?.(a.raw);
                    }}
                    title={`${fmtHM(a.start)}${a.client ? ` • ${a.client}` : ""
                      }`}
                  >
                    <div className="flex items-center gap-1 truncate">
                      <span
                        className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${getStatusDotClass(
                          a.status
                        )}`}
                      />
                      <span className="truncate">
                        {fmtHM(a.start)}
                        {a.client ? ` • ${a.client}` : ""}
                      </span>
                    </div>
                    <div className="text-foreground/60 truncate">
                      {truncateText(a.service || "", 32)}
                      {a.staff ? ` • ${a.staff}` : ""}
                    </div>
                  </div>
                ))}
                {extra > 0 && (
                  <div className="text-[11px] text-foreground/60">
                    +{extra} mai multe…
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =========================
   Wrapper
   ========================= */
/* =========================
   Time-off helpers
   ========================= */
function isOnLeave(timeOffPeriods, staffId, date) {
  if (!timeOffPeriods?.length) return false;
  const d = new Date(date); d.setHours(12, 0, 0, 0);
  return timeOffPeriods.some((r) => {
    if (staffId && staffId !== "all" && r.staffId !== String(staffId)) return false;
    const s = new Date(r.start); s.setHours(0, 0, 0, 0);
    const e = new Date(r.end); e.setHours(23, 59, 59, 999);
    return d >= s && d <= e;
  });
}

function leaveNamesForDay(timeOffPeriods, date) {
  if (!timeOffPeriods?.length) return [];
  const d = new Date(date); d.setHours(12, 0, 0, 0);
  return timeOffPeriods
    .filter((r) => {
      const s = new Date(r.start); s.setHours(0, 0, 0, 0);
      const e = new Date(r.end); e.setHours(23, 59, 59, 999);
      return d >= s && d <= e;
    })
    .map((r) => r.staffName || `#${r.staffId}`);
}

export default function CalendarView({
  view = "week",
  currentDate,
  appointments = [],
  selectedStaff = "all",
  onAppointmentClick,
  onDateSelect,
  workingHours,
  slotMinutes = DEFAULT_SLOT_MINUTES,
  embedded = false,
  timeOffPeriods = [],
}) {

  const workingConfig = useMemo(
    () => buildWorkingConfig(workingHours, slotMinutes),
    [workingHours, slotMinutes]
  );

  if (view === "day") {
    return (
      <DayView
        embedded={embedded}
        date={currentDate}
        items={appointments}
        selectedStaff={selectedStaff}
        onAppointmentClick={onAppointmentClick}
        onDateSelect={onDateSelect}
        workingConfig={workingConfig}
        timeOffPeriods={timeOffPeriods}
      />
    );
  }

  if (view === "month") {
    return (
      <MonthView
        date={currentDate}
        items={appointments}
        selectedStaff={selectedStaff}
        onAppointmentClick={onAppointmentClick}
        onDateSelect={onDateSelect}
        timeOffPeriods={timeOffPeriods}
      />
    );
  }

  // default: week
  return (
    <WeekView
      embedded={embedded}
      date={currentDate}
      items={appointments}
      selectedStaff={selectedStaff}
      onAppointmentClick={onAppointmentClick}
      onDateSelect={onDateSelect}
      workingConfig={workingConfig}
      timeOffPeriods={timeOffPeriods}
    />
  );
}
